import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { GoogleSignInButton, OrDivider } from '../components/GoogleAuthButton';
import '../styles/recovery-design-system.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UnifiedLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextRaw = searchParams.get('next') || '';
  const nextPath = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '/member/library';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already logged in
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('customer_token');
      if (token) {
        console.log('[LOGIN] Existing token found, verifying...');
        try {
          // Verify token is still valid
          await axios.get(`${BACKEND_URL}/api/auth/customer/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('[LOGIN] Token valid, redirecting to library');
          window.location.href = nextPath;
          return;
        } catch (err) {
          console.log('[LOGIN] Token invalid, clearing and showing login');
          localStorage.removeItem('customer_token');
          localStorage.removeItem('customer_id');
          localStorage.removeItem('customer_email');
        }
      }
      
      // Check for auth error from redirect
      const authError = localStorage.getItem('auth_error');
      if (authError) {
        setError(authError);
        toast.error(authError);
        localStorage.removeItem('auth_error');
        console.log('[LOGIN] Displayed auth error from previous session');
      }
      
      setPageLoading(false);
    };
    
    checkExistingAuth();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[LOGIN] Starting login attempt', { email });

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login-password`,
        { email, password }
      );

      console.log('[LOGIN] API Response:', { 
        success: response.data.success, 
        hasToken: !!response.data.access_token,
        hasCustomer: !!response.data.customer
      });

      if (response.data.success && response.data.access_token) {
        // Store the JWT access token
        localStorage.setItem('customer_token', response.data.access_token);
        localStorage.setItem('customer_id', response.data.customer?.id);
        localStorage.setItem('customer_email', response.data.customer?.email);
        
        console.log('[LOGIN] Token stored in localStorage');
        console.log('[LOGIN] Redirecting to /member/library');
        
        toast.success('Welcome back! Loading your dashboard...', { duration: 2000 });
        
        // Immediate redirect
        window.location.href = nextPath;
      } else {
        console.error('[LOGIN] Login succeeded but missing token or customer data');
        setError('Login response invalid. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      console.error('[LOGIN] Login error:', {
        status: err.response?.status,
        detail: err.response?.data?.detail,
        message: err.message
      });
      
      const errorMsg = err.response?.data?.detail || 'Login failed. Please check your credentials and try again.';
      setError(errorMsg);
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/send-magic-link`, { email });
      if (response.data.success) {
        toast.success('Check your email for the login link');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send login link';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--cream)' }}>
      {pageLoading ? (
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto" style={{ color: 'var(--sage)' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: '1rem' }}>Loading...</p>
        </div>
      ) : (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="md" showText={true} />
          </div>
          <h1 style={{ fontFamily: 'var(--font-heading)', color: 'var(--navy)', fontSize: '2rem', marginBottom: '0.5rem' }}>
            Welcome Back
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Login to access your resource library
          </p>
        </div>

        {/* Login Card */}
        <div className="card" style={{ boxShadow: 'var(--shadow-lg)' }}>
          <GoogleSignInButton
            testId="login-google-signin-button"
            onAuthenticated={(data) => {
              localStorage.setItem('customer_token', data.token);
              localStorage.setItem('customer_id', data.customer_id);
              localStorage.setItem('customer_email', data.email);
              toast.success(data.is_new_user ? 'Welcome to Christian Family Recovery!' : 'Welcome back!');
              window.location.href = nextPath;
            }}
          />
          <OrDivider />
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div style={{
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                borderRadius: '8px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'start',
                gap: '0.5rem'
              }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626', marginTop: '2px' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#DC2626', fontSize: '0.875rem', margin: 0, lineHeight: 1.5 }}>
                    {error}
                  </p>
                  {error.includes('No password set') && (
                    <p style={{ color: '#DC2626', fontSize: '0.75rem', margin: '0.5rem 0 0 0', fontStyle: 'italic' }}>
                      Try using "Send Login Link to Email" button below
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label className="label" htmlFor="email">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="password">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </div>
            </div>

            <div className="text-right">
              <Link
                to="/forgot-password"
                style={{ 
                  color: 'var(--sage-dark)',
                  fontSize: '0.875rem',
                  textDecoration: 'none'
                }}
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Logging in...</>
              ) : (
                <><span>Login</span> <ArrowRight className="w-5 h-5" /></>
              )}
            </button>
          </form>

          <button
            onClick={handleMagicLink}
            disabled={loading}
            className="btn-secondary w-full"
          >
            Send Login Link to Email
          </button>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <Link
              to="/"
              style={{ 
                color: 'var(--text-muted)',
                fontSize: '0.875rem',
                textDecoration: 'none'
              }}
            >
              ← Back to Home
            </Link>
          </div>
        </div>

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            New member?{' '}
            <a href="/signup" style={{ color: 'var(--sage-dark)', fontWeight: '600' }}>
              Join now
            </a>
          </p>
        </div>
      </motion.div>
      )}
    </div>
  );
};

export default UnifiedLogin;
